/**
 * Which of a user's legs are on the game a box score shows. The parlays endpoint picks parlays
 * with it and the box score hook picks legs inside them, so the two read one rule.
 */

import { getParentLeague, type LeagueKey, SUPPORTED_LEAGUE_KEYS } from './league';
import { getTeamAbbr } from './teams';
import { type CalendarDay, easternDay, isCalendarDay } from './time';

/** The game a box score shows, as the endpoint's game filter receives it. */
export interface BoxScoreGame {
  /** A tracked leg's Odds API id, or the game picker's provider id, matched by teams and day. */
  gameId: string;
  leagueKey: LeagueKey;
  homeTeam?: string;
  awayTeam?: string;
  /** The Eastern day of the tip, when the caller knows the tip. */
  gameDay?: CalendarDay;
}

/** The leg fields the rule reads: a database row and an API leg both carry them. */
export interface GameLegFields {
  gameId?: string | null;
  team?: string | null;
  opponentTeam?: string | null;
  leagueKey: LeagueKey;
  /** The game day at noon UTC; some legs hold the tip instead (docs/claude/time-rules.md). */
  gameDate?: Date | string | null;
  gameStartsAt?: Date | string | null;
}

/** The Eastern day of a tip or stored game day; undefined for none, `''` or a non-moment. */
export function gameDayOf(value: Date | string | null | undefined): CalendarDay | undefined {
  if (!value) return undefined;
  if (typeof value === 'string' && isCalendarDay(value)) return value;
  const instant = value instanceof Date ? value : new Date(value);
  return Number.isNaN(instant.getTime()) ? undefined : easternDay(instant);
}

function legGameDay(leg: GameLegFields): CalendarDay | undefined {
  return gameDayOf(leg.gameDate ?? leg.gameStartsAt);
}

function teamAbbr(team: string, leagueKey: LeagueKey): string {
  return getTeamAbbr(team, leagueKey).toUpperCase();
}

/**
 * The game's days: `game.gameDay` plus the game day of each leg carrying its id, so a box score
 * opened from a leg, which knows no tip, still dates a team match.
 */
export function gameDaysOf(legs: readonly GameLegFields[], game: BoxScoreGame): Set<CalendarDay> {
  const days = new Set<CalendarDay>();
  if (game.gameDay) days.add(game.gameDay);
  for (const leg of legs) {
    const day = leg.gameId === game.gameId ? legGameDay(leg) : undefined;
    if (day) days.add(day);
  }
  return days;
}

/**
 * The leagues a team match can come from: those sharing the game's parent league, so an NBA
 * preseason leg matches a box score opened under basketball_nba, and an NBA leg never matches a
 * WNBA game whose teams share its abbreviations.
 */
export function gameLeagueKeys(game: BoxScoreGame): LeagueKey[] {
  const parent = getParentLeague(game.leagueKey);
  return SUPPORTED_LEAGUE_KEYS.filter((key) => getParentLeague(key) === parent);
}

/**
 * The legs on the game: those carrying its id, and those in the game's league (`gameLeagueKeys`)
 * whose team and opponent are its two teams on one of its days (`gameDaysOf`).
 */
export function legsOnGame<Leg extends GameLegFields>(
  legs: readonly Leg[],
  game: BoxScoreGame,
): Leg[] {
  const gameDays = gameDaysOf(legs, game);
  const leagueKeys = gameLeagueKeys(game);
  const gameTeams =
    game.homeTeam && game.awayTeam
      ? [teamAbbr(game.homeTeam, game.leagueKey), teamAbbr(game.awayTeam, game.leagueKey)]
      : [];

  const isOnTeamsAndDay = (leg: Leg): boolean => {
    if (gameTeams.length === 0 || !leg.team || !leg.opponentTeam) return false;
    const day = legGameDay(leg);
    return (
      leagueKeys.includes(leg.leagueKey) &&
      day !== undefined &&
      gameDays.has(day) &&
      gameTeams.includes(teamAbbr(leg.team, leg.leagueKey)) &&
      gameTeams.includes(teamAbbr(leg.opponentTeam, leg.leagueKey))
    );
  };

  return legs.filter((leg) => leg.gameId === game.gameId || isOnTeamsAndDay(leg));
}
